'use client'

import React, { useState } from 'react'
import classNames from 'classnames'
import type { AssetConfig } from '../../_assets/types'

/**
 * Asset logo with an automatic fallback to a letter avatar when the
 * MoonPay-hosted SVG 404s. MoonPay's icon CDN doesn't carry an SVG for
 * every supported currency code (TRX, some stablecoin variants, …), so
 * a plain <img> shows the broken-image glyph. This component swaps to
 * a styled circle with the ticker's initial whenever the underlying
 * image errors.
 *
 * Usage:
 *   <AssetIcon asset={asset} size={28} />          // pixel size
 *   <AssetIcon asset={asset} className="size-6" /> // tailwind size class
 *
 * Pass one of `size` (number) or a sizing utility via `className`.
 * `size` doubles as the fallback avatar's font sizing reference.
 */

interface AssetIconProps {
	asset: AssetConfig
	size?: number
	className?: string
}

function AssetIcon({ asset, size, className }: AssetIconProps) {
	const [errored, setErrored] = useState(false)

	if (errored) {
		// Letter avatar fallback. Uses the brand-purple/muted background
		// so it sits comfortably alongside real logos. Font size scales
		// off the explicit pixel size if provided; otherwise the consumer's
		// utility class governs the box and we approximate with em.
		const dim = size ?? undefined
		const fontPx = size ? Math.max(10, Math.round(size * 0.42)) : undefined
		return (
			<div
				className={classNames(
					'inline-flex shrink-0 select-none items-center justify-center rounded-full bg-muted font-semibold text-muted-foreground',
					className
				)}
				style={
					dim
						? { width: dim, height: dim, fontSize: fontPx }
						: { fontSize: '0.5em' }
				}
				aria-hidden
			>
				{asset.symbol.slice(0, 1)}
			</div>
		)
	}

	return (
		<img
			src={asset.iconUrl}
			alt=""
			width={size}
			height={size}
			className={className}
			onError={() => setErrored(true)}
		/>
	)
}

export default AssetIcon
